//brokerintake.js

import { useState } from "react";
import { supabase } from "../supabaseClient";
import DashboardLayout from "../components/BrokerDashboardLayout";
import { CalendarClockIcon } from "lucide-react";
import { MapPin } from "lucide-react";
import { UserRound } from "lucide-react";
import { NotebookText } from "lucide-react";
import { ChevronDown } from "lucide-react";
import PortalFeedback from "../components/PortalFeedback";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";

const AUTH_TIMEOUT_MS = 12000;
const SUBMIT_TIMEOUT_MS = 25000;

const servicesList = [
  "Media: Photo Shoot + Condition Video",
  "Media: Photo Shoot + Condition Video + Marketing Video",
  "Flight: Reposition",
  "Flight: Aircraft Demo",
  "Flight: Delivery Flight",
];

const priorityOptions = [
  { value: "Urgent", label: "Urgent / First Available" },
  { value: "Medium", label: "Normal" },
  { value: "Low", label: "Advisory / Future Scheduling" },
];

const contactRoleOptions = [
  { value: "Aircraft owner", label: "Aircraft owner" },
  { value: "Pilot or Aircraft Manager", label: "Pilot or Aircraft Manager" },
  {
    value: "Third-Party (Line Service, MX Service, FBO)",
    label: "Third-Party (Line Service, MX Service, FBO)",
  },
];

const contactPreferenceOptions = [
  { value: "no preference", label: "No preference" },
  { value: "email", label: "Email" },
  { value: "text", label: "Text" },
  { value: "phone", label: "Phone" },
];

const initialFormData = {
  services: [],
  tail_number: "",
  airport_identifier: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  contact_method_preference: "no preference",
  contact_role: "",
  priority: "",
  notes: "",
};

const isValidContactEmail = (value) => {
  const trimmed = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
};

const isValidContactPhone = (value) => {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");
  const normalizedDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return /^[+\d\s().-]+$/.test(trimmed) && normalizedDigits.length === 10;
};

export default function BrokerIntake() {
  const [formData, setFormData] = useState(initialFormData);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState("");


  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleService = (service) => {
    setFormData((prev) => {
      const exists = prev.services.includes(service);
      return {
        ...prev,
        services: exists
          ? prev.services.filter((s) => s !== service)
          : [...prev.services, service],
      };
    });
  };

  const selectDropdownValue = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setActiveDropdown("");
  };

  const validateForm = () => {
    const missingFieldsMessage = "Please fill out all of the sections to submit";

    if (formData.services.length === 0) return missingFieldsMessage;
    if (!formData.priority) return missingFieldsMessage;
    if (!formData.tail_number.trim()) return missingFieldsMessage;
    if (!formData.airport_identifier.trim()) return missingFieldsMessage;
    if (!formData.contact_name.trim()) return missingFieldsMessage;
    if (!formData.contact_role) return missingFieldsMessage;
    if (!formData.contact_phone.trim() && !formData.contact_email.trim()) {
      return missingFieldsMessage;
    }
    if (formData.contact_phone.trim() && !isValidContactPhone(formData.contact_phone)) {
      return "Please enter a valid 10-digit POC phone number.";
    }
    if (formData.contact_email.trim() && !isValidContactEmail(formData.contact_email)) {
      return "Please enter a valid POC email address.";
    }
    return "";
  };

  const getCurrentUser = async () => {
    const initial = await withTimeout(
      supabase.auth.getUser(),
      AUTH_TIMEOUT_MS,
      "Your session took too long to verify."
    );

    if (initial?.data?.user?.id || initial?.error) {
      return initial;
    }

    await withTimeout(
      supabase.auth.refreshSession(),
      AUTH_TIMEOUT_MS,
      "Your session took too long to refresh."
    );

    return withTimeout(
      supabase.auth.getUser(),
      AUTH_TIMEOUT_MS,
      "Your session took too long to verify."
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError("");
    setSuccessMsg("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const {
        data: { user },
        error: authError,
      } = await getCurrentUser();

      if (authError || !user?.id) {
        setError("You must be signed in to submit a request.");
        return;
      }

      const intakePayload = {
        services: formData.services.join(", "),
        tail_number: formData.tail_number.trim().toUpperCase(),
        airport_identifier: formData.airport_identifier.trim().toUpperCase(),
        contact_name: formData.contact_name.trim(),
        contact_phone: formData.contact_phone.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        contact_method_preference: formData.contact_method_preference,
        contact_role: formData.contact_role,
        priority: formData.priority,
        notes: formData.notes.trim() || null,
        user_id: user.id,
      };

      // 1. Create broker intake.
      const { data: newIntake, error: intakeError } = await withTimeout(
        supabase
          .from("broker_intake")
          .insert([intakePayload])
          .select("id, tail_number")
          .single(),
        SUBMIT_TIMEOUT_MS,
        "The intake request took too long to save."
      );

      if (intakeError) {
        setError(`Intake submission failed: ${intakeError.message}`);
        return;
      }

      // 2. Create the linked tracker row. Only show success after this works.
      const { error: statusError } = await withTimeout(
        supabase
          .from("project_status")
          .insert([{
            intake_id: newIntake.id,
            tail_number: newIntake.tail_number,
            current_status: "Requested",
            priority: intakePayload.priority,
          }])
          .select("id")
          .single(),
        SUBMIT_TIMEOUT_MS,
        "The project tracker took too long to save."
      );

      if (statusError) {
        console.error("Status creation failed:", statusError.message);
        setError(
          `Your request was saved, but the project tracker row could not be created. Please contact support with request ID ${newIntake.id}.`
        );
        return;
      }

      setSuccessMsg("Intake submitted successfully!");
      setFormData(initialFormData);
      setServicesOpen(false);
      setActiveDropdown("");
    } catch (err) {
      if (isTimeoutError(err)) {
        setError(
          "This is taking too long and may be a stale browser session. Refresh this page, confirm you are still signed in, then try again. If you already submitted once, check Projects before submitting again."
        );
      } else {
        setError(err?.message || "Intake submission failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const SelectDropdown = ({ id, name, value, placeholder, options }) => {
    const selected = options.find((option) => option.value === value);
    const isOpen = activeDropdown === id;

    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => {
            setServicesOpen(false);
            setActiveDropdown(isOpen ? "" : id);
          }}
          className="flex w-full items-center justify-between gap-3 rounded-md border border-[#D9D9D9] bg-white px-4 py-3 text-left text-sm text-black shadow-sm"
        >
          <span className={selected ? "text-black" : "text-[#9D9D9D]"}>
            {selected?.label || placeholder}
          </span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-black transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="mt-3 overflow-hidden rounded-xl border border-[#D9D9D9] bg-white shadow-lg">
            {options.map((option) => {
              const selectedOption = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectDropdownValue(name, option.value)}
                  className={`block w-full border-b border-black/5 px-4 py-3 text-left text-sm last:border-b-0 ${
                    selectedOption
                      ? "bg-strobe-red text-white"
                      : "text-black hover:bg-strobe-red/10"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };


return (
  <DashboardLayout>
    <div className="w-full max-w-[1440px] mx-auto text-[#1A1A1A] space-y-2">
      <h1 className="text-2xl font-extrabold tracking-tight">New Service Request</h1>

      <div className="border border-[#D9D9D9] rounded-xl overflow-hidden">
        <form onSubmit={handleSubmit} noValidate>


    {/* SECTION: Requested Services */}
<div className="grid grid-cols-12 gap-6 px-6 py-8">
  {/* LEFT COLUMN */}
  <div className="col-span-12 md:col-span-5 lg:col-span-4 pr-4">
    <div>
      <div className="flex items-center gap-2 mb-2">
        <CalendarClockIcon className="w-4 h-4 text-black/60" />
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          Requested Services
        </h2>
	      </div>
	      <p className="text-sm text-black/50">
	        Please select the desired media and/or flight services from the menu options. Note that multiple services may be selected. Next, select the priority of the request. Normal lead time is approximately 5-7 days.
	      </p>
	    </div>
	  </div>

  {/* RIGHT COLUMN */}
  <div className="col-span-12 md:col-span-7 lg:col-span-8 pl-0 md:pl-16 flex flex-col items-start">

    {/* SERVICE SELECTOR */}
    <div className="w-full">
      <button
        type="button"
        onClick={() => {
          setActiveDropdown("");
          setServicesOpen((open) => !open);
        }}
        className="flex w-full items-center justify-between gap-3 rounded-md border border-[#D9D9D9] bg-white px-4 py-3 text-left text-sm text-black shadow-sm"
      >
        <span className={formData.services.length ? "text-black" : "text-[#9D9D9D]"}>
          {formData.services.length
            ? `${formData.services.length} service${formData.services.length === 1 ? "" : "s"} selected`
            : "Select requested services"}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-black transition-transform ${
            servicesOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {servicesOpen && (
        <div className="mt-3 overflow-hidden rounded-xl border border-[#D9D9D9] bg-white shadow-lg">
          {servicesList.map((service) => {
            const selected = formData.services.includes(service);

            return (
              <label
                key={service}
                className={`flex items-start gap-3 border-b border-black/5 px-4 py-3 text-sm last:border-b-0 ${
                  selected ? "bg-strobe-red text-white" : "text-black"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleService(service)}
                  className="mt-0.5 h-4 w-4 accent-strobe-red"
                />
                <span className="leading-snug">{service}</span>
              </label>
            );
          })}
        </div>
      )}

      {formData.services.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {formData.services.map((service) => (
            <span
              key={service}
              className="rounded-md border border-strobe-red/20 bg-strobe-red/10 px-3 py-1 text-xs text-strobe-red"
            >
              {service}
            </span>
          ))}
        </div>
      )}
    </div>

  {/* TIMING PRIORITY — BELOW BUTTONS */}
<div className="mt-8 w-full md:mt-12">
  <label className="block text-sm font-semibold mb-1">
    Timing Priority:
  </label>

  <SelectDropdown
      id="priority"
      name="priority"
      value={formData.priority}
      placeholder="Select Priority"
      options={priorityOptions}
    />
</div>

  </div>
  </div>
          <hr className="border-[#D9D9D9] mx-6" />

          {/* SECTION: Aircraft Info */}
          <div className="grid grid-cols-12 gap-6 px-6 py-8">
            <div className="col-span-12 md:col-span-5 lg:col-span-4 pr-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-black/60" />
                  <h2 className="text-sm font-semibold tracking-wide uppercase">Aircraft Information</h2>
	                </div>
	                <p className="text-sm text-black/50">
	                  Please enter the aircraft tail number and the ICAO airport identifier where the selected services will be performed. Please note that this location may be different from the aircraft&apos;s home base.
	                </p>
	              </div>
            </div>
            <div className="col-span-12 md:col-span-7 lg:col-span-8 pl-0 md:pl-16 flex flex-col items-start">

              <div className="space-y-6 w-full">
  <div>
    <label className="block text-sm font-semibold mb-1">
        Tail Number:
      </label>
    <input
      type="text"
      name="tail_number"
      value={formData.tail_number}
      onChange={handleChange}
      maxLength={6}
      placeholder="Enter tail number of aircraft e.g. N123AB"
      required
      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
    />
  </div>
  <div>
   <label className="block text-sm font-semibold mb-1">
        Located At:
      </label>
    <input
      type="text"
      name="airport_identifier"
      value={formData.airport_identifier}
      onChange={handleChange}
      maxLength={4}
      placeholder="Enter ICAO that the aircraft is located e.g. KJFK"
      required
      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
    />
  </div>
</div>
            </div>
          </div>
          <hr className="border-[#D9D9D9] mx-6" />

          {/* SECTION: Aircraft Point of Contact */}
          <div className="grid grid-cols-12 gap-6 px-6 py-8">
            <div className="col-span-12 md:col-span-5 lg:col-span-4 pr-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <UserRound className="w-4 h-4 text-black/60" />
                  <h2 className="text-sm font-semibold tracking-wide uppercase">Aircraft Point of Contact</h2>
	                </div>
	                <p className="text-sm text-black/50">
	                  Please provide the requested information for the primary point of contact. Our team will work directly with this individual to arrange scheduling and coordinate aircraft access for the selected services.
	                </p>
	              </div>
            </div>
            <div className="col-span-12 md:col-span-7 lg:col-span-8 pl-0 md:pl-16 flex flex-col items-start">

              <div className="flex flex-col gap-4 w-full">

  {/* Contact Information (stacked) */}
  <div>
    <label className="block text-sm font-semibold mb-1">Contact Information:</label>
    <input
      type="text"
      name="contact_name"
      value={formData.contact_name}
      onChange={handleChange}
      autoComplete="off"
      placeholder="Enter name of POC"
      required
      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
    />
  </div>

  <SelectDropdown
    id="contact_role"
    name="contact_role"
    value={formData.contact_role}
    placeholder="Role of Contact"
    options={contactRoleOptions}
  />


  {/* Contact details — side by side */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      
      <input
        type="tel"
        name="contact_phone"
        value={formData.contact_phone}
        onChange={handleChange}
        autoComplete="off"
        placeholder="POC Phone Number"
        className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
      />
    </div>
    <div>
      
      <input
        type="email"
        name="contact_email"
        value={formData.contact_email}
        onChange={handleChange}
        autoComplete="off"
        placeholder="POC Email"
        className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
      />
    </div>
  </div>

  {/* Contact Preference */}
  <div className="mt-6 w-full">
  <label className="block text-sm font-semibold mb-1">Contact Preference:</label>
  <SelectDropdown
      id="contact_method_preference"
      name="contact_method_preference"
      value={formData.contact_method_preference}
      placeholder="No preference"
      options={contactPreferenceOptions}
    />
</div>

</div>

            </div>
          </div>
          <hr className="border-[#D9D9D9] mx-6" />


          {/* SECTION: Notes */}
          <div className="grid grid-cols-12 gap-6 px-6 py-8">
            <div className="col-span-12 md:col-span-5 lg:col-span-4 pr-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <NotebookText className="w-4 h-4 text-black/60" />
                  <h2 className="text-sm font-semibold tracking-wide uppercase">Notes (Optional)</h2>
	                </div>
	                <p className="text-sm text-black/50">
	                  Please use this section to provide any additional information about the aircraft, its location, or the aircraft point of contact that may assist our team in planning and coordinating services.
	                </p>
	              </div>
            </div>
            <div className="col-span-12 md:col-span-7 lg:col-span-8 pl-0 md:pl-16 flex flex-col items-start">

  <div className="w-full">
    <textarea
      name="notes"
      value={formData.notes}
      onChange={handleChange}
      rows={4}
      placeholder="Tell us what's on your mind..."
      className="w-full px-4 py-2 border border-[#D9D9D9] rounded-md text-sm text-black placeholder-[#9D9D9D]"
    />
  </div>
</div>

          </div>
          

	          {/* Submit + Error Messages */}
	          <div className="px-6 pb-8 space-y-4">
	            {error && <PortalFeedback type="error">{error}</PortalFeedback>}
	            {successMsg && <PortalFeedback type="success">{successMsg}</PortalFeedback>}
	            <button
  type="submit"
  disabled={loading}
  className="mx-auto block bg-strobe-red hover:bg-[#b91c1c] text-white font-semibold px-6 py-3 rounded-md transition"
>

              {loading ? "Submitting..." : "Submit Service Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  </DashboardLayout>
);
}
